/* screens/restaurant/ProfileTab.jsx — профиль заведения (контекст LLM).
   // объединяет: settings/BusinessRestaurantTab.jsx (концепция/формат/гости/тон)
   // + восстановленные поля settings/RestaurantTab.jsx (аллергены гостей, категории, заметки).
   Данные — пропсами (profile, onSave); dirty-флаг → активная «Сохранить»; toast на успех.
   ХЕНДОФФ → frontend/pages/restaurant/ProfileTab.jsx (данные из lib/api/restaurant.js). */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ChipEditor } from '../../components/ui/Chip.jsx';
import { Field, NativeSelect, NumberInput, TextInput, Textarea } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';

  const { useState, useEffect, useCallback, useRef } = React;

  const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  // Фото/логотип заведения. Загрузка немедленная (multipart), вне dirty-формы —
  // чтобы не сбрасывать несохранённые правки текстовых полей. onUpload/onDelete
  // возвращают обновлённый профиль (с photo_url); локально отражаем сразу.
  function PhotoGroup({ photoUrl, onUpload, onDelete }) {
    const toast = useToast();
    const { refreshWorkspaces } = useAuth();
    const [url, setUrl] = useState(photoUrl || null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    useEffect(() => { setUrl(photoUrl || null); }, [photoUrl]);

    const pick = () => { if (fileRef.current) fileRef.current.click(); };
    const onFile = async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      setBusy(true);
      try {
        const updated = await onUpload(file);
        setUrl((updated && updated.photo_url) || null);
        // обновить аватарку пространства в сайдбаре/свитчере (photo_url на ws)
        if (refreshWorkspaces) refreshWorkspaces();
        toast.show('Фото заведения обновлено', 'success');
      } catch (err) {
        toast.show((err && err.detail) || 'Не удалось загрузить фото', 'danger');
      } finally { setBusy(false); }
    };
    const remove = async () => {
      setBusy(true);
      try {
        await onDelete();
        setUrl(null);
        if (refreshWorkspaces) refreshWorkspaces();
        toast.show('Фото заведения удалено', 'muted');
      } catch (err) {
        toast.show('Не удалось удалить фото', 'danger');
      } finally { setBusy(false); }
    };

    return (
      <Group title="Фото заведения" hint="Логотип или фото заведения">
        <div className="avatar-row">
          <span className={'avatar-lg avatar-lg--square' + (url ? ' avatar-lg--photo' : '')} aria-hidden="true">
            {url ? <img src={url} alt="" /> : <Icon name="restaurant" size={28} />}
          </span>
          <div className="avatar-row__actions">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
              hidden onChange={onFile} />
            <div className="cluster cluster-8">
              <Button variant="secondary" size="sm" iconLeft="upload" loading={busy} disabled={busy} onClick={pick}>
                {url ? 'Заменить' : 'Загрузить'}
              </Button>
              {url && <Button variant="ghost" size="sm" iconLeft="trash" disabled={busy} onClick={remove}>Удалить</Button>}
            </div>
            <p className="t-caption text-mute">JPG, PNG или WEBP до 5 МБ.</p>
          </div>
        </div>
      </Group>
    );
  }

  function Group({ title, hint, children }) {
    return (
      <section className="set-card profile-group">
        <div className="set-card__head">
          <h3 className="t-h4">{title}</h3>
          {hint && <span className="t-caption text-mute">{hint}</span>}
        </div>
        <div className="set-card__body">{children}</div>
      </section>
    );
  }

  function ProfileTab({ profile, onSave, onUploadPhoto, onDeletePhoto }) {
    const toast = useToast();
    const [form, setForm] = useState(profile);
    const [base, setBase] = useState(profile);
    const [saving, setSaving] = useState(false);

    // первичная загрузка / внешнее обновление профиля
    useEffect(() => { setForm(profile); setBase(profile); }, [profile]);

    const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);
    const dirty = !eq(form, base);

    const save = async () => {
      setSaving(true);
      try {
        await Promise.resolve(onSave && onSave(form));
        setBase(form);
        toast.show('Профиль сохранён', 'success');
      } catch (e) {
        toast.show('Не удалось сохранить профиль', 'danger');
      } finally { setSaving(false); }
    };

    return (
      <div className="profile-form stack stack-16">
        {onUploadPhoto && (
          <PhotoGroup photoUrl={profile.photo_url} onUpload={onUploadPhoto} onDelete={onDeletePhoto} />
        )}
        <Group title="Концепция и формат" hint="Базовый контекст для ассистента">
          <div className="profile-grid">
            <Field label="Концепция" className="profile-grid__full"
              hint="Опишите идею заведения — её ассистент учитывает при расчётах и предложениях">
              <Textarea value={form.concept || ''} minRows={3} maxRows={8}
                onChange={(e) => set({ concept: e.target.value })}
                placeholder="Например: авторская кухня на основе сезонных локальных продуктов…" />
            </Field>
            <Field label="Формат">
              <NativeSelect value={form.format || ''} aria-label="Формат заведения"
                options={form.format_choices || []}
                onChange={(e) => set({ format: e.target.value })} />
            </Field>
            <Field label="Средний чек">
              <NumberInput value={form.avg_check_rub} unit="₽" min={0}
                onChange={(v) => set({ avg_check_rub: v === '' ? null : Number(v) })} />
            </Field>
            <Field label="Локация">
              <TextInput value={form.location || ''} onChange={(e) => set({ location: e.target.value })}
                placeholder="Город или район" />
            </Field>
            <Field label="Кухни" className="profile-grid__full"
              hint="Теги направлений — Enter добавляет">
              <ChipEditor value={form.cuisine_types || []} onChange={(v) => set({ cuisine_types: v })}
                placeholder="Например: Итальянская, Авторская…" />
            </Field>
          </div>
        </Group>

        <Group title="Гости и тон">
          <div className="profile-grid">
            <Field label="Целевая аудитория" className="profile-grid__full">
              <Textarea value={form.target_audience || ''} minRows={2} maxRows={6}
                onChange={(e) => set({ target_audience: e.target.value })}
                placeholder="Кто ваши гости, что они ценят…" />
            </Field>
            <Field label="Стиль коммуникации" className="profile-grid__full"
              hint="Тон, в котором ассистент формулирует тексты меню и описания">
              <Textarea value={form.brand_voice || ''} minRows={2} maxRows={5}
                onChange={(e) => set({ brand_voice: e.target.value })}
                placeholder="Например: тёплый, уверенный, без пафоса; просто и по делу…" />
            </Field>
          </div>
        </Group>

        <Group title="Заметки технолога" hint="Свободный контекст для ассистента">
          <Field label="Свободные заметки"
            hint="Поставщики, сезонность, особенности производства — всё, что важно учитывать">
            <Textarea value={form.technologist_notes || ''} minRows={3} maxRows={8}
              onChange={(e) => set({ technologist_notes: e.target.value })}
              placeholder="Все соусы — собственного производства. Не использовать пальмовое масло…" />
          </Field>
        </Group>

        <div className="profile-actions">
          {dirty && <span className="profile-actions__hint t-caption"><Icon name="edit" size={13} /> Есть несохранённые изменения</span>}
          {dirty && !saving && (
            <Button variant="ghost" onClick={() => setForm(base)}>Отменить</Button>
          )}
          <Button variant="primary" iconLeft="check" disabled={!dirty || saving} loading={saving} onClick={save}>
            Сохранить
          </Button>
        </div>
      </div>
    );
  }

export { ProfileTab };
